import styled from 'styled-components';
import * as colors from 'utils/constants/theme';
import { templateSlotStyles } from 'views/components/calendar/templateSlot.style';

/**
 * Every capacity cell occupies one fixed block, sized to the tallest shape the
 * table can hold: the figure, the clinician count, and a warning line beneath
 * them. The height is set on the cell itself rather than by padding out the
 * content, so a cell with fewer lines to show still occupies the block and
 * every row comes out the same height whatever its cells say.
 *
 * The cost is deliberate: an unflagged cell carries the warning line's worth of
 * space beneath it. The alternative was folding the warning into the clinician
 * line to keep the block two lines tall, which loses "4 clinicians" — and the
 * count is wanted more than the tighter row.
 *
 * Two things that must hold, or the rows go ragged again:
 *  - a cell with ONE thing to say (an em dash, a clinic name) fills the block
 *    with a single line box of the full height, which centres it in the row.
 *    An empty reserved line instead leaves it hanging at the top of a tall row.
 *  - no line may wrap. A wrapped sub-line takes its row's height with it, so
 *    these truncate and leave the full wording to the cell's tooltip.
 */
const FIGURE_LINE = 22;
const SUB_LINE = 17;
const WARNING_LINE = 17;
const CELL_BLOCK = FIGURE_LINE + SUB_LINE + WARNING_LINE;

/**
 * The section's own header, carried by every Ops page — the clinic table, a
 * single clinic, and a weekday grid are three depths of one thing, and this is
 * what says which part of the app you are in. Type and top spacing mirror the
 * Provider Roster page (pages/provider/list/staff.style.js): one 28px heading
 * 28px below the nav, on the same 1280px measure and 34px gutter as the tables
 * beneath it.
 *
 * It is the page's only `h1`. What follows it names a section rather than the
 * page, so it renders as an `h2` two steps down in size — see TitleWrapper.
 */
export const OpsHeaderWrapper = styled.div`
  background-color: ${colors.WHITE};
  /* The same rule the Clinics page closes its title band with
     (views/components/ui/card/card.style.ts — #e8ecef is that band's own grey,
     not a theme token), so Ops reads as another top-level tab rather than a
     section that forgot its edge. */
  border-bottom: 1px solid #e8ecef;

  .ops-header-inner {
    max-width: 1280px;
    margin: 0 auto;
    /* Bottom padding the Clinics band gets from its 96px min-height: without it
       the rule sits tight under the subtitle. */
    padding: 28px 34px 20px;
  }
  h1 {
    font-size: 28px;
    font-weight: bold;
    letter-spacing: 0.38px;
    margin: 0;
  }
  .ops-header-subtitle {
    font-family: Lato;
    font-size: 15px;
    color: ${colors.DARK_GREY};
    margin: 4px 0 0;
  }
`;

/**
 * The band naming the section beneath the page header, with its action on the
 * right. Its heading is deliberately 18px against the h1's 28px: two headings
 * at the same weight read as a competition, and the page title is the one that
 * says where you are.
 */
export const TitleWrapper = styled.div`
  background-color: ${colors.WHITE};
  position: relative;

  .ops-title-outer-wrap {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    max-width: 1280px;
    margin: 0 auto;
    padding: 18px 34px 0;
    gap: 24px;
    flex-wrap: wrap;
  }
  h2 {
    font-size: 18px;
    font-weight: bold;
    letter-spacing: 0.24px;
    margin: 0;
  }
  .ops-subtitle {
    font-family: Lato;
    font-size: 15px;
    color: ${colors.DARK_GREY};
    margin: 4px 0 0;
    max-width: 62ch;
  }
  .ops-filter {
    flex: none;
    width: 232px;
    padding-top: 4px;
  }
  /* The Provider Roster search gets its height from react-bootstrap-typeahead's
     own form-control styles. This is a plain input — 31 clinics need no async
     lookup — so it has to carry the height itself to match. */
  .ops-filter .rbt-input {
    box-sizing: border-box;
    height: 32px;
    width: 100%;
  }
  /* Chrome draws its own × for type="search"; ours replaces the magnifier at a
     fixed spot, so suppress the native one rather than show two. */
  .ops-filter .rbt-input::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
  }
  /* Inherits the magnifier's positioning from .search-icon-waitList and only
     restyles the button itself. Sized to that icon's own 15×14 and given its
     #415766, so the × lands exactly where the magnifier was and reads as the
     same control rather than something bolted beside it. */
  .ops-search-clear {
    width: 15px;
    height: 14px;
    padding: 0;
    border: 0;
    background: none;
    font-family: Lato;
    font-size: 15px;
    line-height: 14px;
    color: #415766;
    cursor: pointer;
  }
  .ops-search-clear:hover {
    color: ${colors.PURPLE};
  }
  .filter-meta {
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 6px;
    font-family: Lato;
    font-size: 13px;
    color: ${colors.DARK_GREY};
  }
  .filter-meta button {
    background: none;
    border: 0;
    font: inherit;
    color: ${colors.PURPLE};
    cursor: pointer;
    padding: 0;
  }
  .filter-meta button:hover {
    text-decoration: underline;
  }
`;

export const OpsWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 20px 34px 64px;

  /* Table treatment lifted from the CareConnect Hub grid
     (views/containers/datagrid/datagrid-custom.css): one elevated card holding
     a grey header and hairline row separators, rather than each row floating as
     its own bordered box. Those three greys are not in the theme file — they
     are the datagrid's own, repeated here so the two tables read as one family. */
  .table-card {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    overflow: hidden;
    background: ${colors.WHITE};
  }
  .table-scroll {
    width: 100%;
    overflow-x: auto;
  }
  table {
    width: 100%;
    min-width: 720px;
    border-collapse: collapse;
  }

  thead tr {
    background: #f5f5f5;
    height: 50px;
  }
  th {
    font-family: Lato;
    text-align: left;
    font-weight: 600;
    font-size: 1rem;
    color: #222;
    padding: 0 16px;
    white-space: nowrap;
  }
  /* Discipline columns are centred, header and cell alike, so each heading sits
     over the middle of its own column rather than hard against the next one. */
  th.numeric {
    text-align: center;
  }
  .column-head {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }
  .swatch {
    width: 11px;
    height: 11px;
    border-radius: 2px;
    flex: none;
    display: inline-block;
  }

  /* A height on a table cell acts as a minimum, so this is what makes every row
     the same height: each cell holds the block whether or not it has three
     lines to put in it. Top-aligned so the figures sit on one line across the
     row, and 14px rather than 18px because the block is taller than the single
     line that padding was set for.

     No backticks in here — this is inside a styled-components template, and one
     would close the CSS string and break the build. */
  td {
    font-family: Lato;
    height: ${CELL_BLOCK}px;
    padding: 14px 16px;
    font-size: 1rem;
    color: #222;
    vertical-align: top;
    border-bottom: 1px solid #f0f0f0;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  td.numeric {
    text-align: center;
    font-variant-numeric: tabular-nums;
  }
  /* One line centred in the full cell block, so the name sits level with the
     middle of the figures beside it instead of riding the top of the row. */
  .clinic-name {
    display: block;
    line-height: ${CELL_BLOCK}px;
    height: ${CELL_BLOCK}px;
    font-weight: 600;
    color: ${colors.PURPLE};
    text-decoration: none;
  }
  .clinic-name:hover {
    text-decoration: underline;
  }
  /* Spokes are NOT indented: every clinic name starts on the same left edge, so
     the first column reads as one straight edge rather than shifting under the
     rows that happen to be satellites. The hub/spoke relationship is carried by
     row order instead — each hub is followed by its own spokes, and filtering
     keeps the pair together (utils/mappers/response/clinicDtCapacity.ts).

     The single-clinic table below still indents, because there the tree IS the
     content: discipline, then grade, then clinician. Here every row is the same
     kind of thing — a clinic. */
  tr.spoke td:first-child {
    font-weight: normal;
  }

  .open-slots {
    display: block;
    line-height: ${FIGURE_LINE}px;
    height: ${FIGURE_LINE}px;
  }
  .staff-line {
    display: block;
    font-size: 13px;
    line-height: ${SUB_LINE}px;
    height: ${SUB_LINE}px;
    font-weight: normal;
    color: ${colors.DARK_GREY};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* An unstaffed cell has nothing to put on the lower lines, so rather than
     leave the dash hanging at the top of a three-line row, it fills the whole
     block with a single line box, which centres the dash in it. */
  .no-staff {
    display: block;
    line-height: ${CELL_BLOCK}px;
    height: ${CELL_BLOCK}px;
    color: ${colors.DARK_GREY};
  }
  .unconfigured {
    display: block;
    font-size: 13px;
    line-height: ${WARNING_LINE}px;
    font-weight: normal;
    color: ${colors.RED};
  }
  /* Only inside a capacity cell: in the single-clinic table this same class
     carries a longer sentence under a clinician's name, in a column wide enough
     to hold it, where truncating would be a loss rather than a safeguard. */
  td.numeric .unconfigured {
    height: ${WARNING_LINE}px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  td.empty-state {
    text-align: center;
    padding: 32px;
    color: ${colors.DARK_GREY};
    border-bottom: none;
  }
`;

/**
 * The single-clinic view opens on this, with no title band above it, so the
 * table sits near the top of the viewport. Deliberately 13px and muted: a
 * breadcrumb that reads as a headline defeats the point of removing the
 * heading. Same 34px gutter and 1280px measure as the tables it sits above.
 */
export const CrumbsWrapper = styled.div`
  background-color: ${colors.WHITE};

  nav {
    display: flex;
    align-items: center;
    gap: 7px;
    max-width: 1280px;
    margin: 0 auto;
    padding: 16px 34px 0;
    font-family: Lato;
    font-size: 13px;
    color: ${colors.DARK_GREY};
  }
  a {
    color: ${colors.PURPLE};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
  .divider {
    color: ${colors.DARK_GREY};
  }
  [aria-current='page'] {
    font-weight: 600;
    color: #222;
  }
`;

/**
 * The single-clinic table. Same card, header band and hairline separators as
 * the estate-wide one above — they are the same object at two depths, so they
 * carry the same treatment, with depth shown by indentation alone.
 */
export const ClinicOpsWrapper = styled(OpsWrapper)`
  padding-top: 14px;

  table {
    min-width: 900px;
  }
  /* This table's figures stay right-aligned: its columns are Openings, Booked
     and Total over nine narrow columns, where a shared right edge is what lets
     the eye compare one figure against the next. The estate table above centres
     because each discipline column is read on its own. */
  th.numeric,
  td.numeric {
    text-align: right;
  }
  th.day,
  td.day {
    width: 54px;
  }
  td.strong {
    font-weight: 600;
  }
  .zero {
    color: ${colors.DARK_GREY};
    font-weight: normal;
  }

  /* The whole discipline cell is the expander, so the target is the row's
     label rather than a twist arrow a few pixels wide. */
  .twist-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-align: left;
  }
  /* A drawn chevron rather than the ▸/▾ glyphs, which rendered as a hairline a
     few pixels tall and disappeared against the row. Two 2px strokes on a fixed
     14px box: legible at any zoom, identical in every font, and it rotates
     between the two states instead of swapping character. */
  .twist {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    color: #415766;
  }
  .twist::before {
    content: '';
    width: 6px;
    height: 6px;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    border-radius: 1px;
    /* Nudged left by a quarter of its diagonal so the rotated square reads as
       centred in its box rather than hanging off the right of it. */
    margin-left: -2px;
    transform: rotate(-45deg);
    transition: transform 0.15s ease;
  }
  .twist-button[aria-expanded='true'] .twist::before {
    margin-left: 0;
    margin-bottom: -2px;
    transform: rotate(45deg);
  }
  .twist-button:hover .twist {
    color: ${colors.PURPLE};
  }
  .discipline-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
  }

  /* Indentation carries the hierarchy — grade under discipline, clinician
     under grade — with no shading, matching the hub/spoke treatment above.

     One step is 22px: the twist's 14px box plus its 8px gap. A discipline's own
     label already sits 22px in, past its twist, so a grade row starts at 16+44
     and a clinician at 16+66 — each label one clear step right of its parent's.
     At the old 34px a grade label landed 2px LEFT of the discipline label above
     it, because the twist had pushed the parent across, so COTA read as OT's
     sibling rather than as something inside it. */
  tr.depth-1 td:first-child {
    padding-left: 60px;
  }
  tr.depth-2 td:first-child {
    padding-left: 82px;
  }
  tr.depth-2 td {
    font-weight: normal;
  }

  .clinician-name {
    color: ${colors.PURPLE};
    text-decoration: none;
  }
  .clinician-name:hover {
    text-decoration: underline;
  }
  .sublabel {
    display: block;
    font-size: 13px;
    color: ${colors.DARK_GREY};
  }

  /* Link colour and an underline, not a hover-only affordance: the figure is
     the way into that weekday's grid. */
  .weekday-link {
    color: ${colors.PURPLE};
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .weekday-link:hover {
    text-decoration-thickness: 2px;
  }
  .weekday-link .zero {
    color: ${colors.DARK_GREY};
  }

  .table-footnote {
    font-family: Lato;
    font-size: 13px;
    color: ${colors.DARK_GREY};
    margin: 10px 0 0;
    max-width: 88ch;
  }
`;

/**
 * The clinic-wide day grid. The calendar itself is the AXON-3578 template's
 * frame — #b9c0c9 border, #ebebeb header band, 30-minute rows — with clinicians
 * in the columns instead of weekdays, so it reads as the same object.
 */
export const DayGridWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 14px 34px 64px;

  /* The oval day pills the availability form uses for "Apply to days"
     (lib/ui LibOvalCheckboxGroup): 26px circles, purple outline, filled purple
     with white bold text when chosen. Replicated rather than imported because
     that control is a checkbox group and this picks one day at a time. */
  .day-switch {
    display: inline-flex;
    align-items: center;
    margin-bottom: 14px;
  }
  .day-switch .day-oval {
    width: 26px;
    height: 26px;
    margin-right: 4px;
    padding: 0;
    border-radius: 50%;
    border: 1px solid ${colors.PURPLE};
    background: ${colors.WHITE};
    font-family: Lato;
    font-size: 14px;
    font-weight: bold;
    color: #21303a;
    cursor: pointer;
  }
  .day-switch .day-oval:hover {
    background: ${colors.LIGHT_PURPLE};
  }
  .day-switch .day-oval[aria-selected='true'] {
    background: ${colors.PURPLE};
    border-color: ${colors.PURPLE};
    color: ${colors.WHITE};
  }

  /* The grid is wider than the page whenever a clinic staffs more than four or
     five clinicians, so it has to scroll sideways. Hiding the overflow here,
     which the rounded corners wanted, silently clipped the extra columns
     instead and left them unreachable. */
  .grid-card {
    border: 1px solid #b9c0c9;
    border-radius: 2px;
    overflow-x: auto;
    overflow-y: hidden;
    background: ${colors.WHITE};
  }

  /* The time gutter, treated exactly as the provider profile treats its own
     (pages/provider/profile/layout.style.ts): one #e0e0e0 hairline between the
     times and the columns, running the full height including the header cell,
     and each label centred in its row rather than sitting on the line above it.
     Borrowed values, not new ones — the two calendars are the same object seen
     a week or a day at a time.

     Centred across the gutter as well as down it: this gutter is wider than the
     profile's, so a right-aligned label sat oddly far from the times' own
     column. align-items does that, the flex direction here being a column. */
  .clinic-day-grid .rbc-time-header-gutter,
  .clinic-day-grid .rbc-time-gutter {
    border-right: 1px solid #e0e0e0;
  }

  /* One clinician from the next, in the header band as well as in the body
     below it, where the library already draws them. Same #e0e0e0 as the gutter,
     so every line in this calendar is the same line.

     Between columns, not before the first: the gutter's own right border
     already marks that edge, and a border-left there would sit beside it as a
     2px rule. The adjacent-sibling selector is what skips it — each column is
     its own rbc-time-header-content when resources are in play. */
  .clinic-day-grid .rbc-time-header-content + .rbc-time-header-content {
    border-left: 1px solid #e0e0e0;
  }
  /* The library gives EVERY column a left border of its own, so the first one
     lands beside the gutter's right border and the pair reads as a single 2px
     rule, heavier than the lines further along. The gutter already marks that
     edge, so the first column gives its own up. */
  .clinic-day-grid .rbc-time-header-gutter + .rbc-time-header-content {
    border-left: none;
  }

  /* The header's lower edge is drawn on the column heads, so the gutter cell
     beside them had none and the line stopped short of the times — leaving
     12:00 AM with nothing above it. Both halves are given the same #e0e0e0
     here, so the line runs unbroken and in one colour rather than being the
     library's #ddd across the heads and something else over the gutter. */
  .clinic-day-grid .rbc-time-header-gutter,
  .clinic-day-grid .rbc-row-resource .rbc-header {
    border-bottom: 1px solid #e0e0e0;
  }
  .clinic-day-grid .rbc-time-gutter .rbc-time-slot {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  /* Column headers carry the clinician, their grade, and the two figures. */
  /* Dragging happens on the column itself, which is where people reach for it.
     The strip below the filter is a readout of what is loaded, not a control
     for arranging it. */
  /* Every column head the same height, however much it has to say: a clinician
     with a template has no warning line, and their head was ending above their
     neighbours' — a bottom border stopping short, which reads as a column cut
     off rather than a shorter one.

     With resources, react-big-calendar gives each column its OWN
     rbc-time-header-content, so there is no shared row to equalise them. What
     does equalise them is that those columns are flex siblings of
     rbc-time-header and stretch to the tallest. So the height has to be handed
     down the chain — column, resource row, header cell — before the head can
     fill it. Setting flex on the head alone did nothing, because none of its
     ancestors had a height to give.

     The head fills that height rather than being centred in it, because the
     drag drop-target draws an inset shadow down its edge and a short head would
     draw a short bar; its CONTENTS are centred instead (justify-content). */
  .clinic-day-grid .rbc-time-header-content {
    display: flex;
    flex-direction: column;
  }
  .clinic-day-grid .rbc-row-resource {
    flex: 1;
    display: flex;
    min-height: 0;
  }
  .clinic-day-grid .rbc-row-resource .rbc-header {
    flex: 1;
    display: flex;
    min-height: 0;
  }
  .clinician-column-head {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 6px 4px;
    cursor: grab;
    user-select: none;
  }
  .clinician-column-head:active {
    cursor: grabbing;
  }
  .clinician-column-head .col-grip {
    color: ${colors.LIGHT_GREY};
    font-size: 11px;
    line-height: 1;
    letter-spacing: -1px;
  }
  .clinician-column-head:hover .col-grip {
    color: ${colors.PURPLE};
  }
  /* Where the dragged column will land. */
  .clinician-column-head.drop-target {
    box-shadow: inset 3px 0 0 0 ${colors.PURPLE};
  }
  .clinician-column-head.dragging {
    opacity: 0.5;
  }
  .clinician-column-head .clinician-name {
    font-family: Lato;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.PURPLE};
    text-decoration: none;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .clinician-column-head .clinician-name:hover {
    text-decoration: underline;
  }
  .clinician-column-head .grade {
    font-size: 11px;
    color: ${colors.DARK_GREY};
  }
  .clinician-column-head .column-warning {
    font-size: 10px;
    line-height: 13px;
    color: ${colors.RED};
    text-align: center;
    max-width: 100%;
  }
  .clinician-column-head .pills {
    display: inline-flex;
    gap: 4px;
  }
  .pill {
    font-size: 11px;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 10px;
    line-height: 16px;
    white-space: nowrap;
  }
  /* Short of target and met colour by the provider profile's own rule. */
  .pill.short {
    background: #f4b3b3;
    color: #21303a;
  }
  .pill.met {
    background: #d3e9d5;
    color: #21303a;
  }

  /* The slot-* classes stay as the grid's own semantics — what a slot IS — but
     carry no appearance of their own. How a slot LOOKS comes from the shared
     rules below, the same ones the week view uses: a flat green here fought the
     per-appointment-type tint, and a chip background of its own left white text
     on a near-invisible fill. */

  .empty-state {
    font-family: Lato;
    color: ${colors.DARK_GREY};
    padding: 32px;
    text-align: center;
  }
  .grid-footnote {
    font-family: Lato;
    font-size: 13px;
    color: ${colors.DARK_GREY};
    margin: 10px 0 0;
    max-width: 88ch;
  }

  /* The week view's slot rules, not a second set resembling them. Without this
     the grid renders the shared slot markup with no styling for it: the label
     and the time collide on one line, and a pending slot loses the grey dotted
     treatment that is how it reads as unconfirmed. */
  ${templateSlotStyles('.clinic-day-grid')}
`;

/**
 * The clinician picker. The control keeps one position however many chips
 * follow it — pinned left, chips flowing after, Reset pinned right — so
 * choosing who to load never moves the thing you are choosing with.
 */
export const PickerWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;

  .picker {
    position: relative;
    flex: none;
  }
  .picker-button {
    font-family: Lato;
    font-size: 13px;
    color: #222;
    background: ${colors.WHITE};
    border: 1px solid ${colors.LIGHT_GREY};
    border-radius: 4px;
    padding: 6px 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }
  .picker-button:hover,
  .picker-button[aria-expanded='true'] {
    border-color: ${colors.PURPLE};
    color: ${colors.PURPLE};
  }
  .picker-button .caret {
    font-size: 10px;
  }

  .picker-panel {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 60;
    width: 292px;
    background: ${colors.WHITE};
    border: 1px solid ${colors.LIGHT_GREY};
    border-radius: 4px;
    box-shadow: 0 6px 20px rgba(33, 48, 58, 0.14);
    padding: 10px;
  }
  /* Border and radius match the house search control
     (docs/DESIGN-CONVENTIONS.md); the type stays at the popover's 13px rather
     than the page control's 14px/600 — see the note in that doc. */
  .picker-panel input[type='search'] {
    box-sizing: border-box;
    width: 100%;
    font-family: Lato;
    font-size: 13px;
    color: #415766;
    border: 1px solid #c9cfd3;
    border-radius: 8px;
    padding: 7px 10px;
  }
  .picker-panel input[type='search']:focus {
    border-color: ${colors.PURPLE};
    outline: none;
  }
  .picker-list {
    max-height: 288px;
    overflow-y: auto;
    margin-top: 8px;
  }
  .picker-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 4px;
    font-family: Lato;
    font-size: 13px;
    border-radius: 3px;
  }
  .picker-row:hover {
    background: #fafafa;
  }
  .picker-row label {
    flex: 1;
    min-width: 0;
    cursor: pointer;
  }
  .picker-row.group label {
    font-weight: 600;
  }
  .picker-row.child {
    padding-left: 22px;
  }
  .picker-row .count {
    color: ${colors.DARK_GREY};
    font-weight: normal;
  }
  .picker-row .expander {
    background: none;
    border: 0;
    font: inherit;
    font-size: 10px;
    color: ${colors.DARK_GREY};
    cursor: pointer;
    padding: 2px 5px;
    border-radius: 3px;
    flex: none;
  }
  .picker-row .expander:hover {
    color: ${colors.PURPLE};
  }
  .picker-empty {
    font-family: Lato;
    font-size: 13px;
    color: ${colors.DARK_GREY};
    padding: 12px 4px;
    margin: 0;
  }

  .chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: Lato;
    font-size: 13px;
    background: #fafafa;
    border: 1px solid ${colors.LIGHT_GREY};
    border-radius: 999px;
    padding: 3px 6px 3px 10px;
    color: ${colors.DARK_GREY};
  }
  .chip button {
    background: none;
    border: 0;
    font: inherit;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    color: ${colors.DARK_GREY};
    padding: 0 3px;
  }
  .chip button:hover {
    color: ${colors.RED};
  }
  .strip-hint {
    flex: none;
    align-self: center;
    font-family: Lato;
    font-size: 12px;
    color: ${colors.DARK_GREY};
    white-space: nowrap;
  }

  .reset {
    flex: none;
    background: none;
    border: 0;
    font-family: Lato;
    font-size: 13px;
    color: ${colors.PURPLE};
    cursor: pointer;
    padding: 6px 0;
  }
  .reset:hover {
    text-decoration: underline;
  }
`;
